import { Printer } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useCurrency } from '@/hooks/useCurrency'
import { ReportPage, StatGrid, dayLabel, titleCase, useReport, exportCsv, type Column } from '@/components/reports/ReportKit'

interface ZRow {
  day: string
  device: string
  bills: number
  cancelled: number
  gross: number
  discount: number
  serviceCharge: number
  tax: number
  net: number
  billRange: string
  payments: { mode: string; amount: number }[]
}
interface ZData {
  days: ZRow[]
  summary: { gross: number; discount: number; tax: number; net: number; bills: number }
}

export default function ZReportScreen() {
  const { formatCurrency } = useCurrency()
  const report = useReport<ZData>('z-report')
  const data = report.data

  const csvColumns: Column<ZRow>[] = [
    { key: 'day', header: 'Day' },
    { key: 'device', header: 'Terminal' },
    { key: 'billRange', header: 'Bill range' },
    { key: 'bills', header: 'Bills' },
    { key: 'cancelled', header: 'Cancelled' },
    { key: 'gross', header: 'Gross' },
    { key: 'discount', header: 'Discount' },
    { key: 'serviceCharge', header: 'Service charge' },
    { key: 'tax', header: 'Tax' },
    { key: 'net', header: 'Net' },
    { key: 'payments', header: 'Payments', value: r => r.payments.map(p => `${p.mode} ${p.amount}`).join('; ') }
  ]

  const Line = ({ label, value, strong, negative }: { label: string; value: string; strong?: boolean; negative?: boolean }) => (
    <div className={`flex justify-between py-1 text-sm ${strong ? 'border-t pt-2 font-bold' : ''}`}>
      <span className={strong ? '' : 'text-muted-foreground'}>{label}</span>
      <span className={`tabular-nums ${negative ? 'text-red-500 dark:text-red-400' : ''}`}>{value}</span>
    </div>
  )

  return (
    <ReportPage
      eyebrow="Day end · Z-report"
      title="Day-End Z-Report"
      subtitle="Per terminal, per day: gross, tax, discounts, net, payment split and bill range"
      report={report}
      onExport={() => exportCsv('z-report', csvColumns, data?.days ?? [])}
    >
      {data && (
        <>
          <StatGrid
            stats={[
              { label: 'Gross sales', value: formatCurrency(data.summary.gross) },
              { label: 'Discounts', value: formatCurrency(data.summary.discount), tone: data.summary.discount > 0 ? 'bad' : undefined },
              { label: 'Tax collected', value: formatCurrency(data.summary.tax) },
              { label: 'Net sales', value: formatCurrency(data.summary.net), sub: `${data.summary.bills} bills` }
            ]}
          />
          {data.days.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">No sales in this period.</CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {data.days.map(r => (
                <Card key={`${r.day}|${r.device}`} className="print:break-inside-avoid">
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-base font-bold">{dayLabel(r.day)}</div>
                        <div className="text-xs text-muted-foreground">
                          {r.device} · Bills {r.billRange}
                        </div>
                      </div>
                      <div className="text-right text-xs text-muted-foreground">
                        {r.bills} bills
                        {r.cancelled > 0 && <div className="text-red-500 dark:text-red-400">{r.cancelled} cancelled</div>}
                      </div>
                    </div>
                    <div className="mt-3">
                      <Line label="Gross sales" value={formatCurrency(r.gross)} />
                      <Line label="Discounts" value={`-${formatCurrency(r.discount)}`} negative={r.discount > 0} />
                      <Line label="Service charge" value={formatCurrency(r.serviceCharge)} />
                      <Line label="Tax" value={formatCurrency(r.tax)} />
                      <Line label="Net sales" value={formatCurrency(r.net)} strong />
                    </div>
                    {r.payments.length > 0 && (
                      <div className="mt-3 border-t border-dashed pt-3">
                        <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Payment split</div>
                        {r.payments.map(p => (
                          <Line key={p.mode} label={titleCase(p.mode)} value={formatCurrency(p.amount)} />
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          <div className="print:hidden">
            <Button variant="outline" onClick={() => window.print()}>
              <Printer className="mr-2 h-4 w-4" />
              Print
            </Button>
          </div>
        </>
      )}
    </ReportPage>
  )
}
